'use client';

import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';

import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@eliadmin/core/src/components/ui/popover';
import Link from '@eliadmin/core/src/components/link';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** One category root: the row of the left list and its panel, both rendered on the server. */
export type CategoryMenuTab = { value: string; tab: ReactNode; panel: ReactNode };

/**
 * Viewport top of the Figma overlay (226:14636 „Overlay“): the bottom edge of the nav row, followed
 * while the page scrolls under the open menu (0 once the header is scrolled away).
 */
function useOverlayTop(anchor: RefObject<HTMLElement | null>, isOpen: boolean) {
  const [top, setTop] = useState(0);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const update = () => setTop(Math.max(0, anchor.current?.getBoundingClientRect().bottom ?? 0));
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [anchor, isOpen]);

  return top;
}

/**
 * Figma open menu 226:14636: the green „Kategórie“ button turns into „✕ Kategórie“ and opens the
 * panel 229:13449 8 px under the nav row, the width of the row, over the page dimmed by an ink
 * overlay (#0D110D, 50 %). The roots are links: hover or focus previews their panel; a click or Enter opens the category. Escape, a click outside or on a link closes it; focus returns to the button. The
 * nav row (menu, search) is passed as children so the panel can align to it.
 */
export function CategoryMenu({
  label,
  tabs,
  className,
  children,
}: {
  /** Page field „Hlavička - kategórie“. */
  label: string;
  tabs: CategoryMenuTab[];
  className?: string;
  children: ReactNode;
}) {
  const menuId = useId();
  const [isOpen, setOpen] = useState(false);
  const [active, setActive] = useState(tabs[0]?.value ?? '');
  const [portal, setPortal] = useState<HTMLDivElement | null>(null);
  const anchor = useRef<HTMLDivElement>(null);
  const overlayTop = useOverlayTop(anchor, isOpen);

  // Click delegation only: category rows and panel items both keep native link behaviour.
  const closeOnLink = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('a[href]')) setOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverAnchor ref={anchor} className={className}>
        {label && tabs.length > 0 && (
          <PopoverTrigger asChild>
            <Button iconLeft={isOpen ? 'x' : 'squares-four'}>{label}</Button>
          </PopoverTrigger>
        )}
        {children}
      </PopoverAnchor>
      {isOpen && (
        <div
          aria-hidden="true"
          style={{ top: overlayTop }}
          className="fixed inset-x-0 bottom-0 z-40 bg-[#0D110D]/50 duration-200 animate-in fade-in-0 motion-reduce:animate-none"
        />
      )}
      {/* Portal target right after the row: Tab from the open panel continues in page order. */}
      <div ref={setPortal} className="contents" />
      <PopoverContent
        container={portal}
        style={{ maxHeight: `calc(100dvh - ${overlayTop + 16}px)` }}
        align="start"
        sideOffset={8}
        avoidCollisions={false}
        aria-label={label}
        onClick={closeOnLink}
        className="w-[var(--radix-popover-trigger-width)] overflow-y-auto overscroll-contain rounded-2xl border-0 bg-white p-0 shadow-[0_16px_48px_-8px_rgba(13,18,13,0.2)] motion-reduce:animate-none"
      >
        <div className="flex">
          <nav
            aria-label={label}
            onKeyDown={event => {
              if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
              const links = Array.from(
                event.currentTarget.querySelectorAll<HTMLAnchorElement>('a[href]'),
              );
              const index = links.indexOf(event.target as HTMLAnchorElement);
              if (index < 0) return;
              event.preventDefault();
              const next =
                event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? links.length - 1
                    : index + (event.key === 'ArrowDown' ? 1 : -1);
              links[(next + links.length) % links.length]?.focus();
            }}
            className="flex h-auto w-64 shrink-0 flex-col items-stretch justify-start rounded-none bg-[#F5F6F3] px-4 py-6 text-[#0D110D] xl:w-[300px] xl:px-6"
          >
            {tabs.map((tab, index) => (
              <Link
                key={tab.value}
                id={`${menuId}-category-${index}`}
                href={tab.value}
                prefetch={false}
                data-state={active === tab.value ? 'active' : 'inactive'}
                onFocus={() => setActive(tab.value)}
                onMouseEnter={() => setActive(tab.value)}
                className={cn(
                  'group flex min-h-11 justify-start gap-2 whitespace-normal rounded-xl p-3 text-left text-[#0D110D] transition-none',
                  'items-center focus-visible:shadow-focus focus-visible:outline-none',
                  'data-[state=active]:bg-white data-[state=active]:text-[#0E6A20] data-[state=active]:shadow-none',
                )}
              >
                {tab.tab}
              </Link>
            ))}
          </nav>
          {tabs.map((tab, index) => (
            <div
              key={tab.value}
              role="region"
              aria-labelledby={`${menuId}-category-${index}`}
              data-state={active === tab.value ? 'active' : 'inactive'}
              hidden={active !== tab.value}
              tabIndex={0}
              className="mt-0 flex min-w-0 flex-1 rounded-r-2xl focus-visible:shadow-[inset_0_0_0_2px_#127A26] focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=inactive]:hidden"
            >
              {tab.panel}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
